import { useEffect } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconNewEdit from 'src/presentations/components/atom/icons/newedit.svg';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { useFormContext } from 'react-hook-form';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { WeightChargeDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, formatNumber } from 'src/utils/utility';
import { NoContractForm } from './types';
import { getCommonPrefixColumns, getCommonSuffixColumns, formatDateForDisplay } from './commonChargeColumns';

// Common currencies used in freight forwarding
const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'SGD', 'AUD', 'CAD', 'CHF', 'HKD', 'INR', 'MYR', 'THB', 'KRW'];

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid transparent',
  width: '120px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

const useChargesWeightColumns = (): MRT_ColumnDef<WeightChargeDto>[] => {
  const commonPrefix = getCommonPrefixColumns<WeightChargeDto>();
  const commonSuffix = getCommonSuffixColumns<WeightChargeDto>();

  // Weight-specific columns
  const weightSpecific: MRT_ColumnDef<WeightChargeDto>[] = [
    {
      accessorKey: 'minimumPrice',
      header: 'Minimum Price',
      Cell: ({ row }) => {
        const value = row?.original?.minimumPrice;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
      },
    },
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      Cell: ({ row }) => {
        const value = row?.original?.unitPrice;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
        required: true,
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      Cell: ({ row }) => <span className='text-14 xl:text-16'>{row?.original?.currency || '-'}</span>,
      muiEditTextFieldProps: {
        select: true,
        required: true,
        children: CURRENCIES.map((currency) => (
          <MenuItem key={currency} value={currency}>
            {currency}
          </MenuItem>
        )),
      },
    },
    {
      accessorKey: 'chargeUnit',
      header: 'Charge Unit',
      Cell: ({ row }) => <span className='text-14 xl:text-16'>{row?.original?.chargeUnit || '-'}</span>,
      muiEditTextFieldProps: {
        type: 'text',
        required: true,
      },
    },
    {
      accessorKey: 'calculationMethod',
      header: 'Calculation Method',
      Cell: ({ row }) => (
        <span className='text-14 xl:text-16 capitalize'>{row?.original?.calculationMethod ? beautifyEnum(row.original.calculationMethod) : '-'}</span>
      ),
      muiEditTextFieldProps: {
        type: 'text',
      },
    },
    {
      accessorKey: 'minimumChargeableWeight',
      header: 'Minimum Range',
      Cell: ({ row }) => {
        const value = row?.original?.minimumChargeableWeight;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
      },
    },
    {
      accessorKey: 'maximumChargeableWeight',
      header: 'Maximum Range',
      Cell: ({ row }) => {
        const value = row?.original?.maximumChargeableWeight;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
      },
    },
    {
      accessorKey: 'includedQuantity',
      header: 'Included Quantity',
      Cell: ({ row }) => {
        const value = row?.original?.includedQuantity;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Validity Start Date',
      size: 200,
      Cell: ({ row }) => <span className='text-14 xl:text-16'>{formatDateForDisplay(row?.original?.startDate)}</span>,
      Edit: ({ cell, column, row, table }) => {
        const value = cell.getValue<string>();
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={value ? parseISO(value) : null}
              format='dd-MMM-yyyy'
              onChange={(newValue) => {
                row._valuesCache[column.id] = newValue;
                if (table.options.onEditingRowChange) {
                  table.options.onEditingRowChange(row);
                }
              }}
              slotProps={{
                textField: {
                  placeholder: '14-Oct-2025',
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-input': {
                      width: '100px',
                      fontSize: '14px',
                      '@media (min-width: 1536px)': {
                        fontSize: '16px',
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      accessorKey: 'endDate',
      header: 'Validity End Date',
      size: 200,
      Cell: ({ row }) => <span className='text-14 xl:text-16'>{formatDateForDisplay(row?.original?.endDate)}</span>,
      Edit: ({ cell, column, row, table }) => {
        const value = cell.getValue<string>();
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={value ? parseISO(value) : null}
              format='dd-MMM-yyyy'
              onChange={(newValue) => {
                row._valuesCache[column.id] = newValue;
                if (table.options.onEditingRowChange) {
                  table.options.onEditingRowChange(row);
                }
              }}
              slotProps={{
                textField: {
                  placeholder: '14-Oct-2025',
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-input': {
                      width: '100px',
                      fontSize: '14px',
                      '@media (min-width: 1536px)': {
                        fontSize: '16px',
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
  ];

  return [...commonPrefix, ...weightSpecific, ...commonSuffix];
};

export interface ITableChargesWeightProps {
  data: WeightChargeDto[];
  enableEditing?: boolean;
  editingRowIndex?: number | null;
  onEditingRowChange?: (index: number | null) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

export const TableChargesWeight = ({ data, enableEditing = false, editingRowIndex, onEditingRowChange, onEditingChange }: ITableChargesWeightProps) => {
  const columns = useChargesWeightColumns();
  const { setValue } = useFormContext<NoContractForm>();

  const baseTableConfig = createTableConfig<WeightChargeDto>({
    onCreatingCancel: () => {},
    onEditingCancel: () => {},
  });

  console.log('weight: data', data);

  const table = useMaterialReactTable({
    ...baseTableConfig,
    data,
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing,
    editDisplayMode: 'row',
    enableColumnPinning: true,
    positionActionsColumn: 'first',
    initialState: {
      columnPinning: { left: ['mrt-row-actions'] },
    },
    onEditingRowSave: ({ row, values }) => {
      const newData = [...data];
      // Merge edited values with original row to preserve non-editable fields
      newData[row.index] = { ...row.original, ...values };
      setValue('weightCharges', newData);
      table.setEditingRow(null);
      if (onEditingChange) onEditingChange(false);
    },
    onEditingRowCancel: () => {
      table.setEditingRow(null);
      if (onEditingChange) onEditingChange(false);
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        // size: 120,
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton
            onClick={() => {
              table.setEditingRow(row);
              if (onEditingChange) onEditingChange(true);
            }}
          >
            <img src={IconNewEdit} alt='Edit' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton
            color='error'
            onClick={() => {
              const newData = data.filter((_, index) => index !== row.index);
              setValue('weightCharges', newData);
            }}
          >
            <IconTrash />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    columns,
  });

  // Trigger edit mode when editingRowIndex changes
  useEffect(() => {
    if (editingRowIndex !== null && editingRowIndex !== undefined && data[editingRowIndex]) {
      // Get all rows and find the one at the specified index
      const { rows } = table.getRowModel();
      const row = rows[editingRowIndex];
      if (row) {
        table.setEditingRow(row);
        if (onEditingChange) onEditingChange(true);
        if (onEditingRowChange) {
          setTimeout(() => onEditingRowChange(null), 100);
        }
      }
    }
  }, [editingRowIndex, data, table, onEditingRowChange, onEditingChange]);

  return <CustomTable table={table} />;
};
